import type { TaxonomyFieldValue } from '@/entities/taxonomy';
import { usableOptions } from './applyRules';
import { at } from './lookup';

type Option = TaxonomyFieldValue;

function raw(text: string): string {
  return text;
}

function exact(text: string): string {
  return text.normalize('NFC');
}

/**
 * Full-width and half-width forms, the Latin digraphs ĳ, ǆ, ǉ, ǌ and ǳ, and the
 * Latin and Armenian ligatures. Folded one character at a time: NFKC on the
 * whole text would also fold symbols that mean something else (m² to m2, ① to
 * 1, ™ to TM). Written as escapes because the characters are hard to read.
 */
const WIDTH_FORMS =
  /[\u0132\u0133\u01C4-\u01CC\u01F1-\u01F3\u0587\uFB00-\uFB06\uFB13-\uFB17\uFF01-\uFFEE]/g;

function foldWidth(text: string): string {
  return text.replace(WIDTH_FORMS, (c) => c.normalize('NFKC')).normalize('NFC');
}

/**
 * Also drops invisible format characters (zero-width space, soft hyphen) that
 * web copies carry. They go before NFC, so a mark they separated from its letter
 * composes with it.
 */
function caseless(text: string): string {
  return text
    .replace(/\p{Cf}/gu, '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function widthless(text: string): string {
  return caseless(foldWidth(text));
}

/**
 * Stripping every combining mark can make two different words equal (мой and
 * мои), which is why this form is tried last.
 */
function markless(text: string): string {
  return foldWidth(text)
    .normalize('NFD')
    .replace(/[\p{M}\p{Cf}]/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

interface Step {
  key: (text: string) => string;
  fields: readonly ('value' | 'code')[];
}

/** Strictest first: the first step that fits any option decides, unless the rules forbid all it fits. */
const steps: readonly Step[] = [
  { key: raw, fields: ['value'] },
  { key: exact, fields: ['value'] },
  { key: raw, fields: ['code'] },
  { key: exact, fields: ['code'] },
  { key: caseless, fields: ['value', 'code'] },
  { key: widthless, fields: ['value', 'code'] },
  { key: markless, fields: ['value', 'code'] },
];

type VocabularyIndex = { step: Step; buckets: Map<string, Option[]> }[];

function buildIndex(source: readonly Option[]): VocabularyIndex {
  const index = steps.map((step) => ({ step, buckets: new Map<string, Option[]>() }));
  for (const option of usableOptions(source)) {
    for (const { step, buckets } of index) {
      for (const key of new Set(step.fields.map((field) => step.key(option[field])))) {
        if (!key) {
          continue;
        }
        const bucket = buckets.get(key);
        if (bucket) {
          bucket.push(option);
        } else {
          buckets.set(key, [option]);
        }
      }
    }
  }
  return index;
}

/**
 * Keyed by the source array: a resolved list's `values` and a field's `values`
 * keep their identity between renders, so one paste builds the index once
 * rather than once per pasted cell.
 */
const indexes = new WeakMap<readonly Option[], VocabularyIndex>();

function indexOf(source: readonly Option[]): VocabularyIndex {
  let index = indexes.get(source);
  if (!index) {
    index = buildIndex(source);
    indexes.set(source, index);
  }
  return index;
}

function sameValue(matches: Option[]): boolean {
  const first = at(matches, 0);
  return first !== undefined && matches.every((o) => exact(o.value) === exact(first.value));
}

/**
 * The option of `source` a pasted value stands for, or `null` when there is none.
 *
 * Tried in order: the value byte for byte, then as NFC; the code byte for byte,
 * then as NFC; then value or code ignoring case, whitespace runs and
 * invisible format characters; then also ignoring width and ligatures; then
 * also ignoring combining marks (accents,
 * vowel signs, dakuten). A step that fits options with different values refuses
 * the paste. Otherwise it returns the first option it fits that `allowed`
 * accepts, and when `allowed` accepts none of them the next step is tried. So
 * ambiguity is judged on the whole of `source`, never only on what is allowed.
 * Letters with no separate mark (đ, ł, ø, ß, ı) are not folded.
 */
export function matchVocabulary(
  source: readonly Option[],
  pasted: string,
  allowed: (option: Option) => boolean = () => true,
): Option | null {
  const token = pasted.trim();
  if (!token) {
    return null;
  }
  for (const { step, buckets } of indexOf(source)) {
    const key = step.key(token);
    const matches = key ? buckets.get(key) : undefined;
    if (!matches) {
      continue;
    }
    if (!sameValue(matches)) {
      return null;
    }
    const pick = matches.find(allowed);
    if (pick) {
      return pick;
    }
  }
  return null;
}
